import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Plus, Pencil, Trash2, ChevronUp, ChevronDown, X, Eye, EyeOff } from "lucide-react";
import { api, resolveImg } from "@/lib/api";
import { toast } from "sonner";
import { AdminShell } from "@/components/admin/AdminShell";
import { Field } from "@/components/admin/Field";
import { SCHEMAS } from "@/components/admin/schemas";

function slugify(s) {
  return (s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

export default function CollectionManager() {
  const { name } = useParams();
  const schema = SCHEMAS[name];
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // object being edited (null = closed)
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get(`/admin/col/${name}`).then((r) => setItems(r.data)).catch(() => {}), [name]);
  useEffect(() => { setItems([]); load(); }, [load]);

  if (!schema) return <AdminShell title="Not found"><div /></AdminShell>;

  const startNew = () => {
    const blank = { published: true };
    schema.fields.forEach((f) => { if (f.type === "list") blank[f.key] = []; });
    setEditing(blank);
  };

  const save = async () => {
    const data = { ...editing };
    if (schema.idKey === "slug" && !data.slug) data.slug = slugify(data[schema.labelKey]);
    setSaving(true);
    try {
      if (data.id) await api.put(`/admin/col/${name}/${data.id}`, data);
      else await api.post(`/admin/col/${name}`, data);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item[schema.labelKey]}"? This cannot be undone.`)) return;
    await api.delete(`/admin/col/${name}/${item.id}`);
    toast.success("Deleted");
    load();
  };

  const togglePublish = async (item) => {
    await api.put(`/admin/col/${name}/${item.id}`, { ...item, published: !(item.published !== false) });
    load();
  };

  const move = async (index, dir) => {
    const next = [...items];
    const j = index + dir;
    if (j < 0 || j >= next.length) return;
    [next[index], next[j]] = [next[j], next[index]];
    setItems(next);
    await api.post(`/admin/col/${name}/reorder`, { ids: next.map((i) => i.id) });
  };

  return (
    <AdminShell title={schema.title} subtitle={schema.subtitle}
      actions={<button onClick={startNew} className="flex items-center gap-2 bg-espresso text-ivory px-6 py-3 rounded-full text-xs uppercase tracking-[0.14em] hover:bg-espressosoft" data-testid="collection-add"><Plus size={15} /> Add New</button>}>
      <div className="space-y-3">
        {items.map((item, i) => (
          <div key={item.id} className="bg-ivory border border-espresso/10 rounded-md p-4 flex items-center gap-4" data-testid={`collection-item-${item.id}`}>
            <div className="flex flex-col">
              <button onClick={() => move(i, -1)} className="text-taupe hover:text-espresso disabled:opacity-30" disabled={i === 0}><ChevronUp size={16} /></button>
              <button onClick={() => move(i, 1)} className="text-taupe hover:text-espresso disabled:opacity-30" disabled={i === items.length - 1}><ChevronDown size={16} /></button>
            </div>
            {(item.image || item.cover_image) && (
              <img src={resolveImg(item.image || item.cover_image)} alt="" className="w-16 h-16 object-cover rounded" />
            )}
            <div className="flex-1 min-w-0">
              <div className="font-serif text-xl text-espresso truncate">{item[schema.labelKey] || "Untitled"}</div>
              <div className="text-xs text-taupe">{item.category || item.role || item.who || item.location || ""}</div>
            </div>
            <button onClick={() => togglePublish(item)} title="Toggle visibility" className={`p-2 rounded ${item.published !== false ? "text-sage" : "text-taupe/50"}`} data-testid={`collection-publish-${item.id}`}>
              {item.published !== false ? <Eye size={18} /> : <EyeOff size={18} />}
            </button>
            <button onClick={() => setEditing(item)} className="p-2 text-espresso hover:text-taupe" data-testid={`collection-edit-${item.id}`}><Pencil size={17} /></button>
            <button onClick={() => remove(item)} className="p-2 text-destructive/60 hover:text-destructive" data-testid={`collection-delete-${item.id}`}><Trash2 size={17} /></button>
          </div>
        ))}
        {items.length === 0 && <p className="text-taupe">Nothing here yet. Click "Add New" to create your first entry.</p>}
      </div>

      {/* Slide-over editor */}
      {editing && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="collection-editor">
          <div className="absolute inset-0 bg-espresso/40" onClick={() => setEditing(null)} />
          <div className="relative w-full max-w-2xl bg-linen h-full overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-linen border-b border-espresso/10 px-8 py-5 flex items-center justify-between z-10">
              <h2 className="font-serif text-2xl text-espresso">{editing.id ? "Edit" : "New"} {schema.title.replace(/s$/, "")}</h2>
              <div className="flex items-center gap-3">
                <button onClick={save} disabled={saving} className="bg-espresso text-ivory px-6 py-2.5 rounded-full text-xs uppercase tracking-[0.14em] disabled:opacity-60" data-testid="collection-save">{saving ? "Saving…" : "Save"}</button>
                <button onClick={() => setEditing(null)} className="text-espresso"><X size={22} /></button>
              </div>
            </div>
            <div className="p-8 space-y-5">
              {schema.fields.map((f) => (
                <Field key={f.key} def={f} value={editing[f.key]} onChange={(v) => setEditing((p) => ({ ...p, [f.key]: v }))} />
              ))}
            </div>
          </div>
        </div>
      )}
    </AdminShell>
  );
}
